import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';

/**
 * Service to manage shared recurrence state across billing components.
 *
 * This service provides a centralized way to manage the payment recurrence
 * (monthly/yearly) state that is shared between:
 * - Choose Plan Generic Component
 * - Plan Comparison Component
 * - Payment Recurrence Tabs Component
 * - Upgrade/Downgrade Plan Component
 *
 * When any component changes the recurrence, all other components
 * will automatically be notified and updated.
 */
@Injectable({
  providedIn: 'root',
})
export class RecurrenceStateService {
  private readonly _payRecurrence$ = new BehaviorSubject<PricePeriodicity>('year');

  /**
   * Observable for the current pay recurrence state
   * Subscribe to this to get notified when the recurrence changes
   */
  get payRecurrence$(): Observable<PricePeriodicity> {
    return this._payRecurrence$.asObservable();
  }

  /**
   * Get the current pay recurrence value synchronously
   */
  get payRecurrence(): PricePeriodicity {
    return this._payRecurrence$.value;
  }

  /**
   * Set the pay recurrence value and notify all subscribers
   * @param recurrence - The new recurrence value ('month' or 'year')
   */
  setPayRecurrence(recurrence: PricePeriodicity): void {
    if (!recurrence || !['month', 'year'].includes(recurrence)) {
      console.warn('Invalid recurrence value:', recurrence);
      return;
    }

    if (this._payRecurrence$.value !== recurrence) {
      this._payRecurrence$.next(recurrence);
    }
  }

  /**
   * Reset the recurrence state to default (yearly)
   */
  reset(): void {
    this._payRecurrence$.next('year');
  }

  /**
   * Get the current state as a snapshot
   * Useful for debugging or logging
   */
  getStateSnapshot(): { currentValue: PricePeriodicity, subscribers: number } {
    return {
      currentValue: this._payRecurrence$.value,
      subscribers: this._payRecurrence$.observers.length,
    };
  }
}
